import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Box, Grid, Card, CardContent, Button, Stack, Chip, Tabs, Tab, Divider, CircularProgress } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';
import ScienceIcon from '@mui/icons-material/Science';
import CodeIcon from '@mui/icons-material/Code';
import { useProjects } from '../../hooks/useProjects';
import SEO from '../../components/seo/SEO';

import aiImg from "../../assets/activities/conference.png";
import devImg from "../../assets/activities/training.png";
import labImg from "../../assets/activities/meetup.png";

import EditableImage from '../../components/ui/EditableImage';

const ProjectsPage = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const { data: projectsData = [], isLoading } = useProjects({ 
    language: i18n.language,
    isPublished: true,
    limit: 50
  });

  const filteredItems = activeTab === 0 
    ? projectsData 
    : projectsData.filter(item => item.type === (activeTab === 1 ? 'project' : 'lab'));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getFallbackImage = (type) => {
    if (type === 'project') return devImg;
    if (type === 'lab') return labImg;
    return aiImg;
  };

  return (
    <Box component="main" sx={{ bgcolor: '#0B0F14', minHeight: '100vh', pb: 20 }}>
      <SEO 
        title={t('projects.seo.title', { defaultValue: 'Projets' })}
        description={t('projects.seo.description', { defaultValue: 'Découvrez mes projets en Data Science, Machine Learning et développement web. Portfolio de solutions IA innovantes.' })}
        keywords="projets, data science, machine learning, IA, portfolio, Python, React"
      />
      {/* Header */}
      <Box sx={{ py: 15, bgcolor: '#111827', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="overline" 
              align="center"
              sx={{ color: '#0FA3A3', fontWeight: 700, letterSpacing: 3, display: 'block', mb: 2 }}
            >
              {t("projects.header.overline")}
            </Typography>
            <Typography 
              variant="h1" 
              align="center"
              sx={{ color: 'white', mb: 3, fontFamily: 'Philosopher', fontSize: { xs: '2.5rem', md: '3.5rem' } }}
            >
              {t("projects.header.titlePrefix")} <span style={{ color: '#D4AF37' }}>{t("projects.header.titleHighlight")}</span>
            </Typography>
            <Typography 
              variant="h6" 
              align="center"
              sx={{ color: '#9CA3AF', fontWeight: 300, lineHeight: 1.6, maxWidth: '700px', mx: 'auto' }}
            >
              {t("projects.header.subtitle")}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 8 }}>
        {/* Navigation Tabs */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 8 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': { bgcolor: '#D4AF37', height: 3 },
              '& .MuiTab-root': { 
                color: '#9CA3AF', 
                fontSize: '1.1rem', 
                fontWeight: 700,
                px: 4,
                '&.Mui-selected': { color: 'white' }
              }
            }}
          >
            <Tab label={t("projects.tabs.all")} />
            <Tab label={t("projects.tabs.projects")} icon={<CodeIcon />} iconPosition="start" />
            <Tab label={t("projects.tabs.labs")} icon={<ScienceIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#D4AF37' }} />
          </Box>
        ) : (
          /* Grid Items */
          <Grid container spacing={4}>
            <AnimatePresence mode="popLayout">
              {filteredItems.map((item) => (
                <Grid item xs={12} md={6} lg={4} key={item.id}>
                  <motion.div
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Card sx={{ 
                      height: '100%', 
                      bgcolor: '#1F2937', 
                      borderRadius: '24px',
                      border: '1px solid rgba(255,255,255,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: item.type === 'project' ? '#0FA3A3' : '#D4AF37',
                        transform: 'translateY(-10px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
                      }
                    }}>
                      <Box sx={{ height: '200px', position: 'relative', overflow: 'hidden' }}>
                        <EditableImage 
                          src={item.cover_image_url || getFallbackImage(item.type)}
                          alt={item.title}
                          tableName="projects"
                          recordId={item.id}
                          imageField="cover_image_url"
                          sx={{ width: '100%', height: '100%' }}
                          imgSx={{ filter: 'brightness(0.7)' }}
                        />
                        <Chip 
                          label={(item.type === 'project' ? t("projects.tabs.projects") : t("projects.tabs.labs")).toUpperCase()} 
                          sx={{ 
                            position: 'absolute', 
                            top: 16, 
                            right: 16,
                            bgcolor: item.type === 'project' ? '#0FA3A3' : '#D4AF37',
                            color: item.type === 'project' ? 'white' : '#0B0F14',
                            fontWeight: 800,
                            fontSize: '0.7rem',
                            zIndex: 1
                          }} 
                        />
                      </Box>

                      <CardContent sx={{ p: 4, flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, mb: 2, fontFamily: 'Philosopher' }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3, lineHeight: 1.6 }}>
                          {item.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                          {item.technologies?.map(techRel => (
                            <Chip 
                              key={techRel.technology.id} 
                              label={techRel.technology.name} 
                              size="small" 
                              sx={{ 
                                bgcolor: 'rgba(255,255,255,0.03)', 
                                color: '#9CA3AF', 
                                border: '1px solid rgba(255,255,255,0.1)',
                                fontSize: '0.7rem'
                              }} 
                            />
                          ))}
                        </Box>
                      </CardContent>

                      <Box sx={{ p: 4, pt: 0 }}>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 3 }} />
                        <Stack direction="row" spacing={2}>
                          {item.github_url && (
                            <Button 
                              variant="outlined" 
                              startIcon={<GitHubIcon />}
                              fullWidth
                              href={item.github_url}
                              target="_blank"
                              sx={{ 
                                borderRadius: '10px', 
                                borderColor: 'rgba(255,255,255,0.1)',
                                color: 'white',
                                textTransform: 'none',
                                fontWeight: 700,
                                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.05)' }
                              }}
                            >
                              {t("projects.card.code")}
                            </Button>
                          )}
                          {item.live_url && (
                            <Button 
                              variant="contained" 
                              startIcon={<LaunchIcon />}
                              fullWidth
                              href={item.live_url}
                              target="_blank"
                              sx={{ 
                                borderRadius: '10px', 
                                bgcolor: item.type === 'project' ? '#0FA3A3' : '#D4AF37',
                                color: item.type === 'project' ? 'white' : '#0B0F14',
                                textTransform: 'none',
                                fontWeight: 700,
                                '&:hover': { bgcolor: item.type === 'project' ? '#0C8A8A' : '#B8962E' }
                              }}
                            >
                              {t("projects.card.demo")}
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        )}

        {/* Call to Action */}
        <Box 
          sx={{ 
            mt: 12, 
            p: { xs: 4, md: 8 }, 
            bgcolor: '#111827', 
            borderRadius: '32px', 
            border: '1px solid rgba(255,255,255,0.05)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute', 
              top: -100, 
              right: -100, 
              width: 300, 
              height: 300, 
              bgcolor: 'rgba(15, 163, 163, 0.05)', 
              borderRadius: '50%', 
              filter: 'blur(80px)' 
            }} 
          />
          <Typography variant="h3" sx={{ color: 'white', mb: 3, fontFamily: 'Philosopher', fontWeight: 700 }}>
            {t("projects.cta.titlePrefix")} <span style={{ color: '#0FA3A3' }}>{t("projects.cta.titleHighlight")}</span>
          </Typography>
          <Typography variant="h6" sx={{ color: '#9CA3AF', mb: 5, maxWidth: '600px', mx: 'auto', fontWeight: 300 }}>
            {t("projects.cta.subtitle")}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button 
              variant="contained" 
              size="large"
              onClick={() => window.location.href='/contact'}
              sx={{ 
                bgcolor: '#0FA3A3', 
                color: 'white', 
                px: 6, 
                py: 2, 
                borderRadius: '12px',
                fontWeight: 800,
                fontSize: '1.1rem',
                textTransform: 'none',
                '&:hover': { bgcolor: '#0C8A8A' }
              }}
            >
              {t("projects.cta.primaryButton")}
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              onClick={() => window.location.href='/services'}
              sx={{ 
                borderColor: 'rgba(255,255,255,0.1)', 
                color: 'white', 
                px: 6, 
                py: 2, 
                borderRadius: '12px',
                fontWeight: 800,
                fontSize: '1.1rem',
                textTransform: 'none',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.05)' }
              }}
            >
              {t("projects.cta.secondaryButton")}
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default ProjectsPage;
